/**
 * Who decides a module is finished.
 *
 * The contract is `docs/completion-truth.md`, written before this file. Read it
 * first: these functions are deliberately thin, and the reason each one is
 * shaped the way it is lives there.
 *
 * The short version. There used to be two records of "this trainee has
 * finished this module" — the run in localStorage and the progress document —
 * and the local one both drew the screen and was the one that could be wrong.
 * Three symptoms came out of that, and three separate patches each introduced
 * a new bug. So the second source of truth is gone: the server decides, and
 * localStorage holds answers only.
 *
 * These are pure functions rather than conditions inlined into the components
 * on purpose. The same decision has to be made by a paged lesson and a sheet
 * module, the previous version made it slightly differently in each, and a
 * hand-edited JSX condition is not something a test can hold on to.
 */

import { aggregateResults, isReissuePending, toMillis } from './lessonScoring.js'

/**
 * Which screen a lesson should draw: 'waiting', 'finished' or 'reading'.
 *
 * `progress` is the module's progress document as the context holds it —
 * already optimistically merged on write and rolled back if the write failed,
 * so `completed === true` means "the server has it, or is about to".
 *
 * `justFinished` is this page session having finished a run. It is the one
 * thing that can draw the finished screen without the server, and it lasts
 * exactly as long as the session does: that is what makes a failed write
 * resumable on the next load instead of a permanent lie (C2).
 *
 * `retaking` is the trainee having pressed "Take it again". It has to outrank
 * the server, or the button does nothing at all — they ARE completed, so C1
 * would draw the completion screen straight back over the lesson. It is an
 * intent held for this session, not a claim about what has been recorded, so an
 * abandoned retake leaves the original completion standing.
 *
 * `waiting` matters more than it looks. Deciding while progress is still
 * loading drew the lesson and then corrected itself, which is both a flash and
 * the reason a finished read-through looked like a slow page rather than a bug.
 */
export function lessonScreen({
  loading = false, progress = null, justFinished = false, retaking = false,
} = {}) {
  if (loading) return 'waiting'
  // Before `retaking`, so finishing the retake lands on the completion screen
  // rather than being held open by the intent that started it.
  if (justFinished) return 'finished'
  if (retaking) return 'reading'
  // Strictly true. A reset and a reissue both DELETE this field rather than
  // setting it false, so anything looser starts reading a deleted field's
  // absence as a value.
  if (progress?.completed === true) return 'finished'
  return 'reading'
}

/**
 * The numbers on the completion screen, taken from the server's record.
 *
 * The stored `percentageScore` is used rather than recomputed from the stored
 * answers. Recomputing would let the trainee's screen and the admin console
 * disagree the moment the scoring rules change, and the console shows the
 * stored value. `correctCount` is the one thing the server does not store, so
 * it is the one thing derived.
 *
 * Returns null when a scored module claims completion with nothing stored
 * behind it — a legacy document (KNOWN-ISSUES 5). The screen then says the
 * module is complete without inventing a 0%, which would read as a failure the
 * trainee never earned.
 */
export function summaryFromProgress(progress, { total = 0, passingScore = 0.7, unscored = false } = {}) {
  // A declared read-through stores no score, and its completion is its whole
  // record. Reuse aggregateResults on an empty run so the shape the screen
  // renders is identical either way.
  if (unscored) return aggregateResults([], 0, passingScore)

  const stored = progress?.quizResults
  if (!stored || typeof stored.percentageScore !== 'number') return null

  const questions = Array.isArray(stored.questions) ? stored.questions : []
  const maxPossibleScore = typeof stored.maxPossibleScore === 'number' ? stored.maxPossibleScore : total

  return {
    totalScore: typeof stored.totalScore === 'number' ? stored.totalScore : 0,
    maxPossibleScore,
    percentageScore: stored.percentageScore,
    // Guarded on the divisor for the same reason aggregateResults is: a module
    // with nothing to mark must not pass on a 1/0.
    passed: maxPossibleScore > 0 && stored.percentageScore >= passingScore,
    correctCount: questions.filter((q) => q?.correct).length,
    answeredCount: questions.length,
  }
}

/**
 * Should the answers held in this browser be thrown away?
 *
 * The only question left for the server to settle about local state, now that
 * local state is answers rather than verdicts: an admin wiping the module means
 * start again, whatever stage the trainee had reached.
 *
 * A MISSING document is deliberately not evidence of a wipe. Reading it as one
 * is what the old `isRunReset` did, and it is how a first render with a null
 * uid — which reads localStorage under the `anon` key and finds nothing —
 * could discard a run that was perfectly good.
 */
export function isLocalRunStale(progress, savedAt = 0) {
  if (!progress) return false
  if (toMillis(progress.resetAt) > savedAt) return true
  return toMillis(progress.reissued) > savedAt
}

/**
 * The sheet-check verdict to show, and whether to ask for a retest.
 *
 * One reader, one input. The bug this replaces was two reads racing — a
 * staleness check clearing the verdict and a prefill effect restoring it — so
 * the trainee could see "all checks passed" and "your trainer has asked you to
 * do this again" at once. Derived from a single value, cleared and restored
 * cannot both be true.
 *
 * `sessionResult` is a check run on this page. It outranks the stored verdict
 * because it is newer, and it satisfies any retest request it just answered.
 */
export function sheetVerdict({ loading = false, progress = null, sessionResult = null } = {}) {
  if (loading) return { verdict: null, retestDue: false }
  if (sessionResult) return { verdict: sessionResult, retestDue: false }

  // A pass with no completion beside it. Either an admin cleared the
  // completion, or the verdict was written and the completion write that
  // should have followed it did not land. Both mean the verdict no longer
  // stands and the trainee has to press the button again.
  const stalePass = progress?.checkResult?.passed === true && progress?.completed !== true

  if (isReissuePending(progress) || stalePass) return { verdict: null, retestDue: true }

  return { verdict: progress?.checkResult ?? null, retestDue: false }
}
